import { formatScore, type RoundEntry, type Score } from '../model/types.js';
import { ROUND_COLUMN, ROUND_WIDTH } from './parse.js';
import type { TrfDocument, TrfPlayer, TrfScoreKey } from './types.js';

/** Header codes in the order TRF files conventionally list them. */
const HEADER_ORDER = [
  '012',
  '022',
  '032',
  '042',
  '052',
  '062',
  '072',
  '082',
  '092',
  '102',
  '112',
  '122',
  '132',
];

function put(buf: string[], from1: number, text: string, width: number, align: 'l' | 'r'): void {
  const t = text.length > width ? text.slice(0, width) : text;
  const padded = align === 'r' ? t.padStart(width, ' ') : t.padEnd(width, ' ');
  for (let i = 0; i < width; i++) buf[from1 - 1 + i] = padded.charAt(i);
}

function roundBlock(r: RoundEntry): string {
  const opp = r.opponent === null ? '0000' : String(r.opponent).padStart(4, ' ');
  const col = r.colour ?? '-';
  let res: string = r.result;
  if (res === '*') res = ' ';
  return `${opp} ${col} ${res}`;
}

/** Format one 001 player line. */
export function formatPlayerLine(p: TrfPlayer): string {
  const width = ROUND_COLUMN + p.rounds.length * ROUND_WIDTH;
  const buf: string[] = new Array<string>(Math.max(width, 89)).fill(' ');
  put(buf, 1, '001', 3, 'l');
  put(buf, 5, String(p.startRank), 4, 'r');
  put(buf, 10, p.sex === 'f' ? 'w' : p.sex === 'm' ? 'm' : '', 1, 'l');
  put(buf, 11, p.title, 3, 'r');
  put(buf, 15, p.name, 33, 'l');
  put(buf, 49, p.rating > 0 ? String(p.rating) : '', 4, 'r');
  put(buf, 54, p.federation, 3, 'l');
  put(buf, 58, p.fideId, 11, 'r');
  put(buf, 70, p.birthDate, 10, 'l');
  put(buf, 81, Number.isNaN(p.points) ? '' : formatScore(p.points), 4, 'r');
  put(buf, 86, p.rank > 0 ? String(p.rank) : '', 4, 'r');
  p.rounds.forEach((r, i) => {
    const block = roundBlock(r);
    const col = ROUND_COLUMN + i * ROUND_WIDTH;
    for (let k = 0; k < block.length; k++) buf[col + k] = block.charAt(k);
  });
  return buf.join('').replace(/\s+$/, '');
}

export interface WriteTrfOptions {
  /**
   * Which extension records to write for pairing data:
   * 'trf26' (default) writes TRF26 records (142, 152, 162, 192, 202, 212,
   * 240, 250, 260, 299, 310); 'trfx' writes the legacy JaVaFo/bbpPairings
   * lines (XXR, XXC, XXA, XXP, BB*); 'none' writes only TRF-16 content.
   */
  readonly format?: 'trf26' | 'trfx' | 'none';
  /** @deprecated use format; true means 'trfx'. */
  readonly engineExtensions?: boolean;
}

function fmtPts(v: Score, width: number): string {
  return formatScore(v).padStart(width, ' ');
}

function idList(ids: readonly number[], width = 4, sep = ' '): string {
  return ids.map((n) => String(n).padStart(width, ' ')).join(sep);
}

function writeTrf26Records(doc: TrfDocument, out: string[]): void {
  if (doc.totalRounds !== null) out.push(`142 ${doc.totalRounds}`);
  if (doc.initialColour !== null) out.push(`152 ${doc.initialColour.toUpperCase()}`);
  const keys: TrfScoreKey[] = ['W', 'D', 'L', 'A', 'P', 'X'];
  const parts = keys.filter((k) => doc.scoring[k] !== undefined);
  if (parts.length > 0) {
    // key at column 6, points at 7-10, next pair 9 columns later
    const buf: string[] = new Array<string>(10 + 9 * (parts.length - 1)).fill(' ');
    put(buf, 1, '162', 3, 'l');
    parts.forEach((k, i) => {
      put(buf, 6 + 9 * i, k, 1, 'l');
      put(buf, 7 + 9 * i, formatScore(doc.scoring[k] as Score), 4, 'r');
    });
    out.push(buf.join('').replace(/\s+$/, ''));
  }
  if (doc.tournamentType !== null) out.push(`192 ${doc.tournamentType}`);
  if (doc.tiebreaks.length > 0) out.push(`202 ${doc.tiebreaks.join(',')}`);
  if (doc.standings.length > 0) out.push(`212 ${doc.standings.join(',')}`);
  for (const b of doc.byes) {
    out.push(
      `240 ${b.kind ?? ' '} ${String(b.round).padStart(3, '0')} ${idList(b.ids)}`.replace(
        /\s+$/,
        '',
      ),
    );
  }
  for (const a of doc.accelerations) {
    out.push(
      `250 ${fmtPts(a.matchPoints, 4)} ${fmtPts(a.points, 4)} ${String(a.firstRound).padStart(3, '0')} ${String(a.lastRound).padStart(3, '0')} ${String(a.firstId).padStart(4, '0')} ${String(a.lastId).padStart(4, '0')}`,
    );
  }
  for (const f of doc.forbidden) {
    out.push(
      `260 ${String(f.firstRound).padStart(3, '0')} ${String(Math.min(f.lastRound, 999)).padStart(3, '0')} ${idList(f.ids)}`,
    );
  }
  for (const a of doc.abnormalPoints) {
    const buf: string[] = new Array<string>(23).fill(' ');
    put(buf, 1, '299', 3, 'l');
    put(buf, 5, a.type, 1, 'l');
    put(buf, 8, a.matchPoints === null ? '' : formatScore(a.matchPoints), 4, 'r');
    put(buf, 14, a.points === null ? '' : formatScore(a.points), 4, 'r');
    put(buf, 20, a.round === 0 ? '' : String(a.round).padStart(3, '0'), 3, 'l');
    out.push((buf.join('') + idList(a.ids)).replace(/\s+$/, ''));
  }
}

function writeTrfxRecords(doc: TrfDocument, out: string[]): void {
  if (doc.totalRounds !== null) out.push(`XXR ${doc.totalRounds}`);
  const xxc: string[] = [];
  if (doc.useRankAsPairingId) xxc.push('rank');
  if (doc.initialColour === 'w') xxc.push('white1');
  if (doc.initialColour === 'b') xxc.push('black1');
  if (xxc.length > 0) out.push(`XXC ${xxc.join(' ')}`);
  // XXA: one line per player with per-round points from column 10.
  const perPlayer = new Map<number, Score[]>();
  for (const a of doc.accelerations) {
    for (let id = a.firstId; id <= a.lastId; id++) {
      const arr = perPlayer.get(id) ?? [];
      for (let r = a.firstRound; r <= a.lastRound; r++) {
        while (arr.length < r) arr.push(0);
        arr[r - 1] = (arr[r - 1] ?? 0) + a.points;
      }
      perPlayer.set(id, arr);
    }
  }
  for (const [id, pts] of [...perPlayer.entries()].sort((x, y) => x[0] - y[0])) {
    out.push(`XXA ${String(id).padStart(4, ' ')}${pts.map((p) => fmtPts(p, 5)).join('')}`);
  }
  for (const f of doc.forbidden) out.push(`XXP ${f.ids.join(' ')}`);
  const bb: [TrfScoreKey, string][] = [
    ['W', 'BBW'],
    ['D', 'BBD'],
    ['L', 'BBL'],
    ['A', 'BBZ'],
    ['A', 'BBF'],
    ['P', 'BBU'],
  ];
  for (const [k, code] of bb) {
    const v = doc.scoring[k];
    if (v !== undefined) out.push(`${code} ${fmtPts(v, 4)}`);
  }
  if (doc.absentNextRound.length > 0) out.push(`XXZ ${doc.absentNextRound.join(' ')}`);
}

/** Serialise a TRF document. Output uses LF line endings and a trailing newline. */
export function writeTrf(doc: TrfDocument, options: WriteTrfOptions = {}): string {
  const format = options.format ?? (options.engineExtensions ? 'trfx' : 'trf26');
  const out: string[] = [];
  const written = new Set<string>();
  for (const code of HEADER_ORDER) {
    const v = doc.headers.get(code);
    if (v !== undefined) {
      out.push(`${code} ${v}`.replace(/\s+$/, ''));
      written.add(code);
    }
  }
  for (const [code, v] of doc.headers) {
    if (!written.has(code)) out.push(`${code} ${v}`.replace(/\s+$/, ''));
  }
  if (format === 'trf26') writeTrf26Records(doc, out);
  const players = [...doc.players].sort((a, b) => a.startRank - b.startRank);
  for (const p of players) out.push(formatPlayerLine(p));
  for (const n of doc.nationalRatings) {
    const buf: string[] = new Array<string>(79).fill(' ');
    put(buf, 1, n.federation, 3, 'l');
    put(buf, 5, String(n.startRank), 4, 'r');
    put(buf, 10, n.sex, 1, 'l');
    put(buf, 11, n.classification, 3, 'l');
    put(buf, 15, n.name, 33, 'l');
    put(buf, 49, n.rating > 0 ? String(n.rating) : '', 4, 'r');
    put(buf, 54, n.origin, 3, 'l');
    put(buf, 58, n.nationalId, 11, 'r');
    put(buf, 70, n.birthDate, 10, 'l');
    out.push(buf.join('').replace(/\s+$/, ''));
  }
  for (const t of doc.teams) {
    if (format === 'trf26' && t.pairingNumber !== undefined) {
      const buf: string[] = new Array<string>(73 + t.members.length * 5).fill(' ');
      put(buf, 1, '310', 3, 'l');
      put(buf, 5, String(t.pairingNumber), 3, 'r');
      put(buf, 9, t.name, 32, 'l');
      put(buf, 42, t.nickname ?? '', 5, 'l');
      put(buf, 55, formatScore(t.matchPoints ?? 0), 6, 'r');
      put(buf, 62, formatScore(t.gamePoints ?? 0), 6, 'r');
      put(buf, 69, t.rank ? String(t.rank) : '', 3, 'r');
      t.members.forEach((m, i) => put(buf, 74 + i * 5, String(m), 4, 'r'));
      out.push(buf.join('').replace(/\s+$/, ''));
    } else {
      const buf: string[] = new Array<string>(36 + t.members.length * 5).fill(' ');
      put(buf, 1, '013', 3, 'l');
      put(buf, 5, t.name, 32, 'l');
      t.members.forEach((m, i) => put(buf, 37 + i * 5, String(m), 4, 'r'));
      out.push(buf.join('').replace(/\s+$/, ''));
    }
  }
  if (format === 'trfx') writeTrfxRecords(doc, out);
  for (const l of doc.otherLines) out.push(l);
  return out.join('\n') + '\n';
}
